// El VUELO de la biblioteca de adjuntos: lo que se toca SE VA de la grilla y viaja
// hasta su chip en la caja de prompt —el mismo gesto del Catálogo, donde el producto
// elegido sale de la grilla y aterriza en la tira (`PickProductPanel`)—
// (ADR-2026-09-12-lo-adjuntado-se-muda-de-la-grilla-a-la-caja).
//
// Cómo se arma, en el mismo orden que allá:
//  1. En el click, ANTES de adjuntar, se mide la miniatura (después ya no está en el
//     DOM: la grilla esconde lo que está adjunto).
//  2. Adjuntar y `despegar` van en el mismo handler: React los aplica en un solo
//     commit, así que cuando corre el layout effect la miniatura ya no está y el chip
//     ya existe. Ahí se crea el clon en el origen y se ESCONDE el chip (antes de
//     pintar: sin eso se vería un frame y desaparecería).
//  3. Dos frames después se mide el chip —la caja crece con la fila de chips y eso
//     corre después de pintar— y arranca el vuelo.
//  4. Al aterrizar, primero se muestra el chip y después se borra el clon: al revés
//     hay un frame sin nada, que se ve como un parpadeo. El chip no aparece de golpe:
//     se DESPLIEGA desde su miniatura hacia la derecha (un `clip-path` que se abre),
//     así el nombre y la X salen de la imagen que acaba de llegar
//     (ADR-2026-09-12-las-cards-se-acomodan-y-el-chip-se-despliega).
//
// El clon es DOM puro colgado del `body`: ningún re-render lo saca, así que el cleanup
// del efecto lo borra si el panel se desmonta o si sale otro vuelo antes de que éste
// llegue. Con `prefers-reduced-motion` no se despega nada: el adjunto ya quedó.

import { useLayoutEffect, useState } from 'react';

/** La caja de prompt: el `BarraEntradaChat` la marca con este atributo. */
export const ATRIBUTO_CAJA = 'data-caja-del-agente';
/**
 * Valor del atributo de la caja ESPEJO: la que la biblioteca dibuja al pie cuando está
 * abierta encima del chat (modo lienzo). Si existe, el vuelo aterriza ahí y no en la
 * caja real, que queda tapada (ADR-2026-09-16-con-el-lienzo-la-biblioteca-espeja-la-caja).
 */
export const CAJA_ESPEJO = 'espejo';
/** Cada chip de adjunto de la caja lleva este atributo con el id del adjunto. */
export const ATRIBUTO_CHIP = 'data-chip-adjunto';

export interface VueloPendiente {
  /** Id del adjunto: con él se encuentra el chip de destino. */
  id: string;
  /** Dónde estaba la miniatura al tocarla. */
  origen: DOMRect;
  /** La imagen que viaja. */
  src: string;
  /** El radio de la miniatura, para que el clon sea igual. */
  radio: string;
}

export const prefiereMenosMovimiento = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Prende el chip AL INSTANTE, sin que una transición heredada lo funda. */
const mostrar = (el: HTMLElement | null) => {
  if (!el) return;
  el.style.transition = 'none';
  el.style.opacity = '';
  void el.offsetHeight;
  el.style.transition = '';
};

export function useVueloALaCaja(): { despegar: (vuelo: VueloPendiente) => void } {
  const [enVuelo, setEnVuelo] = useState<VueloPendiente | null>(null);

  useLayoutEffect(() => {
    if (!enVuelo || typeof document === 'undefined') return;
    const vuelo = enVuelo;

    // Primero la caja espejo (la biblioteca tapa a la real en modo lienzo); si no hay,
    // la caja de siempre.
    const selectorChip = `[${ATRIBUTO_CHIP}="${CSS.escape(vuelo.id)}"]`;
    const chip =
      document.querySelector<HTMLElement>(
        `[${ATRIBUTO_CAJA}="${CAJA_ESPEJO}"] ${selectorChip}`,
      ) ?? document.querySelector<HTMLElement>(`[${ATRIBUTO_CAJA}] ${selectorChip}`);
    if (!chip) {
      setEnVuelo(null);
      return;
    }
    chip.style.opacity = '0';

    const clon = document.createElement('div');
    clon.style.cssText = [
      'position:fixed',
      'left:0',
      'top:0',
      `width:${vuelo.origen.width}px`,
      `height:${vuelo.origen.height}px`,
      `border-radius:${vuelo.radio}`,
      'overflow:hidden',
      'background:#fff',
      'box-shadow:0 10px 30px rgba(0,0,0,.20)',
      'pointer-events:none',
      'z-index:9999',
      'transform-origin:top left',
      `transform:translate(${vuelo.origen.left}px, ${vuelo.origen.top}px)`,
    ].join(';');
    const img = document.createElement('img');
    img.src = vuelo.src;
    img.alt = '';
    img.style.cssText = 'width:100%;height:100%;object-fit:cover';
    clon.appendChild(img);
    document.body.appendChild(clon);

    let anim: Animation | null = null;
    /** El chip se abre desde la miniatura hacia la derecha; sin miniatura, se funde. */
    const desplegar = () => {
      if (typeof chip.animate !== 'function') return;
      const foto = chip.querySelector<HTMLElement>('img');
      if (!foto) {
        chip.animate(
          [
            { opacity: 0, transform: 'scale(0.9)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: 200, easing: 'ease-out' },
        );
        return;
      }
      const chipRect = chip.getBoundingClientRect();
      const fotoRect = foto.getBoundingClientRect();
      // Lo que queda a la vista al arrancar: la miniatura con su margen izquierdo.
      const visible = fotoRect.right - chipRect.left + (fotoRect.left - chipRect.left);
      const radio = getComputedStyle(chip).borderRadius || '12px';
      chip.animate(
        [
          { clipPath: `inset(0 calc(100% - ${visible}px) 0 0 round ${radio})` },
          { clipPath: `inset(0 0 0 0 round ${radio})` },
        ],
        { duration: 260, easing: 'cubic-bezier(.2,.7,.25,1)' },
      );
    };
    const aterrizar = () => {
      mostrar(chip);
      clon.remove();
      desplegar();
      setEnVuelo(null);
    };
    const arrancar = () => {
      // Aterriza sobre la miniatura del chip si la tiene; si no, sobre el chip.
      const destinoEl = chip.querySelector<HTMLElement>('img') ?? chip;
      destinoEl.scrollIntoView({ block: 'nearest' });
      const destino = destinoEl.getBoundingClientRect();
      const escala = destino.width / vuelo.origen.width;
      if (typeof clon.animate !== 'function') {
        aterrizar();
        return;
      }
      anim = clon.animate(
        [
          { transform: `translate(${vuelo.origen.left}px, ${vuelo.origen.top}px) scale(1)` },
          { transform: `translate(${destino.left}px, ${destino.top}px) scale(${escala})` },
        ],
        { duration: 380, easing: 'cubic-bezier(.2,.7,.25,1)', fill: 'forwards' },
      );
      anim.onfinish = aterrizar;
      // Si no puede correr (pestaña en background), el chip tiene que aparecer igual.
      anim.oncancel = aterrizar;
    };
    let segundoFrame = 0;
    const primerFrame = requestAnimationFrame(() => {
      segundoFrame = requestAnimationFrame(arrancar);
    });

    return () => {
      cancelAnimationFrame(primerFrame);
      cancelAnimationFrame(segundoFrame);
      if (anim) {
        anim.onfinish = null;
        anim.oncancel = null;
        anim.cancel();
      }
      mostrar(chip);
      clon.remove();
    };
  }, [enVuelo]);

  return { despegar: setEnVuelo };
}
